import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/client.js";

// Modal for bulk-importing audit paras from a CSV file.
// Admins only (SUPER_ADMIN, CORP_ADMIN).
export default function BulkImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Lock background page scroll while modal is mounted.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  const submit = async () => {
    if (!file) {
      setError("Please choose a CSV or Excel (.xlsx) file first.");
      return;
    }
    const name = (file.name || "").toLowerCase();
    const ok = name.endsWith(".csv") || name.endsWith(".xlsx") || name.endsWith(".xls");
    if (!ok) {
      setError(
        "Only CSV (.csv) and Excel (.xlsx/.xls) files are supported. " +
        "If you have a .numbers, PDF, or Word file, please export it to CSV or Excel first."
      );
      return;
    }
    setError("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/paras/bulk-import/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  // Build & download a CSV of the rows that didn't import (rejected first,
  // then skipped duplicates) so the user can fix and re-upload them.
  const downloadReport = () => {
    const rejected = (result?.rejected_records || []).map((r) => ({ ...r, type: "Rejected" }));
    const skipped = (result?.skipped_records || []).map((r) => ({ ...r, type: "Skipped" }));
    const rows = [...rejected, ...skipped];
    if (rows.length === 0) return;

    const headers = ["Type", "Row", "S.No", "Audit Year", "Para No", "Description", "Reason"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      headers.map(esc).join(","),
      ...rows.map((r) =>
        [r.type, r.row, r.s_no, r.audit_year, r.para_no, r.description, r.reason]
          .map(esc)
          .join(",")
      ),
    ];
    // Prepend a UTF-8 BOM so Excel renders Tamil text correctly.
    const csv = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bulk-import-skipped-rejected-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = (type) => {
    // Common required columns for every template.
    const CORE = ["s_no", "audit_year", "para_no", "description", "amount"];
    const CORE_SAMPLE = [
      "1", "2024-25", "AP-001",
      "Full para description here", "50000",
    ];

    let headers, sample;

    if (type === "zone") {
      // Zone CUG (Zonal AC) — assignment by zone_name.
      headers = [...CORE, "zone_name"];
      sample = [...CORE_SAMPLE, "North Zone"];
    } else if (type === "department") {
      // HOD CUG (Department Head) — assignment by department.
      headers = [...CORE, "department"];
      sample = [...CORE_SAMPLE, "Engineering"];
    } else if (type === "officer") {
      // Individual officer — assignment by name + mobile.
      headers = [...CORE, "officer_name", "officer_mobile"];
      sample = [...CORE_SAMPLE, "Officer Full Name", "9000000001"];
    } else {
      // Basic (no assignment) — just required + amount.
      headers = [...CORE];
      sample = [...CORE_SAMPLE];
    }

    const csv = headers.join(",") + "\n" + sample.map((v) => `"${v}"`).join(",");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-paras-template-${type}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-wide"
        onClick={(e) => e.stopPropagation()}
        style={{ padding: 0 }}
      >
        {/* Sticky header */}
        <div style={{
          position: "sticky",
          top: 0,
          padding: "12px 20px",
          borderBottom: "1px solid #e2e8f0",
          background: "#fff",
          zIndex: 2,
        }}>
          <h3 style={{ margin: "0 0 2px", color: "#0f172a", fontSize: "15px", fontWeight: 700 }}>
            📤 Bulk Import Paras
          </h3>
          <p style={{ margin: 0, fontSize: "11.5px", color: "#64748b" }}>
            Upload a CSV or Excel (.xlsx) file — with optional auto-assignment.
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: "14px 20px" }}>

        {!result ? (
          <>
            {error && (
              <div style={{
                background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "8px",
                padding: "10px 14px", marginBottom: "14px", fontSize: "12.5px", color: "#b91c1c",
              }} data-testid="bulk-import-error">
                ⚠️ {error}
              </div>
            )}

            {/* ── Column reference (always open) ── */}
            <div className="csv-ref-card">
              <div className="csv-ref-title">
                📋 CSV Column Reference
                <span className="csv-ref-subtitle">(required + optional columns)</span>
              </div>

              <div className="csv-ref-groups">
                <div className="csv-ref-group">
                  <div className="csv-ref-group-label csv-ref-required">Required</div>
                  <div className="csv-ref-chips">
                    {["s_no", "audit_year", "para_no", "description"].map((col) => (
                      <code key={col} className="csv-chip csv-chip-red">{col}</code>
                    ))}
                  </div>
                </div>
                <div className="csv-ref-group">
                  <div className="csv-ref-group-label csv-ref-optional">Optional</div>
                  <div className="csv-ref-chips">
                    {["amount", "fund"].map((col) => (
                      <code key={col} className="csv-chip csv-chip-green">{col}</code>
                    ))}
                  </div>
                </div>
                <div className="csv-ref-group">
                  <div className="csv-ref-group-label csv-ref-autoassign">🔗 Assignment (any one)</div>
                  <div className="csv-ref-chips">
                    {["zone_name", "department", "officer_mobile", "officer_name"].map((col) => (
                      <code key={col} className="csv-chip csv-chip-blue">{col}</code>
                    ))}
                  </div>
                </div>
              </div>

              <div className="csv-ref-notes">
                <div>🏢 <code>zone_name</code> → assigned to the Zonal AC (CUG). For multi-zone, use comma-separated names (e.g. <code>"North Zone, South Zone"</code>). Shorthands accepted: <code>EZ/WZ/SZ/NZ/CZ</code>, <code>all zone</code> (every zone), <code>rev all zone</code> (every zone + Revenue), <code>main office</code> (HQ — no zone).</div>
                <div>🏛️ <code>department</code> → assigned to the Department Head (HOD CUG). Codes in the zone column also work: <code>CHO</code>→Health, <code>ACCT</code>→Accounts, <code>TPO</code>→Town Planning, <code>WS</code>→Water Supply.</div>
                <div>👤 <code>officer_mobile</code> / <code>officer_name</code> → assigned directly to that officer.</div>
                <div>💡 Priority: officer → zone → department. Unrecognized codes are kept as the department name and imported unassigned (never dropped).</div>
                <div>🇮🇳 Tamil headers are accepted: <code>வ. எண்</code>, <code>தணிக்கை ஆண்டு</code>, <code>பத்தி எண்</code>, <code>பத்தி சுருக்கம்</code>, <code>இழப்புத் தொகை</code>, <code>மண்டலம்</code>, <code>துறை</code>, <code>நிதி</code>.</div>
              </div>
            </div>

            {/* ── Download Templates ── */}
            <div className="bulk-templates">
              <div className="bulk-templates-title">⬇ Download Template</div>
              <div className="bulk-templates-grid">
                <button
                  className="btn-outline bulk-tpl-btn"
                  onClick={() => downloadTemplate("zone")}
                  data-testid="download-template-btn"
                >
                  🏢 Zone (AC CUG)
                </button>
                <button
                  className="btn-outline bulk-tpl-btn"
                  onClick={() => downloadTemplate("department")}
                >
                  🏛️ Department (HOD CUG)
                </button>
                <button
                  className="btn-outline bulk-tpl-btn"
                  onClick={() => downloadTemplate("officer")}
                >
                  👤 Officer (direct)
                </button>
              </div>
            </div>

            {/* ── File Upload ── */}
            <label className={"file-upload" + (file ? " has-file" : "")}>
              <input
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                data-testid="bulk-import-file-input"
              />
              <span className="file-upload-icon">
                <i className={file ? "ti ti-file-check" : "ti ti-cloud-upload"}></i>
              </span>
              <span className="file-upload-text">
                {file ? (
                  <>
                    <strong>{file.name}</strong>
                    <small>{(file.size / 1024).toFixed(1)} KB · Tap to replace</small>
                  </>
                ) : (
                  <>
                    <strong>Choose a CSV / Excel file</strong>
                    <small>or drag &amp; drop here</small>
                  </>
                )}
              </span>
            </label>

            <div
              className="modal-actions"
              style={{
                position: "sticky",
                bottom: 0,
                marginLeft: "-20px",
                marginRight: "-20px",
                marginBottom: "-14px",
                padding: "10px 20px",
                background: "#fff",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                zIndex: 2,
              }}
            >
              <button className="btn" onClick={onClose} data-testid="bulk-cancel-btn">
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={busy || !file}
                onClick={submit}
                data-testid="bulk-import-submit-btn"
                style={{
                  background: "#1e3a8a", color: "#fff", border: "none",
                  padding: "8px 20px", borderRadius: "7px", fontWeight: 600,
                  fontSize: "13px", cursor: busy || !file ? "not-allowed" : "pointer",
                  opacity: busy || !file ? 0.6 : 1,
                }}
              >
                {busy ? "⏳ Importing..." : "🚀 Import Now"}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* ── Result Screen ── */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: "10px", marginBottom: "16px",
            }}>
              {[
                { label: "Created", value: result.created, icon: "✅", color: "#15803d", bg: "#f0fdf4" },
                { label: "Auto-Assigned", value: result.assigned || 0, icon: "🔗", color: "#1e40af", bg: "#eff6ff" },
                { label: "Skipped", value: result.skipped, icon: "⏭️", color: "#92400e", bg: "#fffbeb" },
                { label: "Errors", value: result.total_errors || 0, icon: "❌", color: "#b91c1c", bg: "#fef2f2" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  style={{
                    background: stat.bg, border: `1px solid ${stat.color}30`,
                    borderTop: `3px solid ${stat.color}`,
                    borderRadius: "8px", padding: "12px 14px", textAlign: "center",
                  }}
                  data-testid={stat.label === "Created" ? "bulk-result" : undefined}
                >
                  <div style={{ fontSize: "20px", marginBottom: 4 }}>{stat.icon}</div>
                  <div style={{ fontSize: "22px", fontWeight: 800, color: stat.color }}>
                    {stat.value}
                  </div>
                  <div style={{
                    fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
                    color: "#64748b", letterSpacing: "0.05em", marginTop: 2,
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {result.assigned > 0 && (
              <div style={{
                background: "#eff6ff", border: "1px solid #bfdbfe",
                borderRadius: "8px", padding: "10px 14px", marginBottom: "12px",
                fontSize: "12px", color: "#1e40af",
              }}>
                🔗 <strong>{result.assigned}</strong> para(s) were auto-assigned and assignees notified.
              </div>
            )}

            {result.skipped_details && result.skipped_details.length > 0 && (
              <div style={{
                background: "#fffbeb", borderRadius: "8px", padding: "12px 14px",
                maxHeight: 160, overflowY: "auto", marginBottom: "12px",
              }} data-testid="bulk-skipped-details">
                <h4 style={{ marginBottom: 8, fontSize: "12px", color: "#92400e" }}>
                  ⏭️ Skipped rows (duplicates)
                  {result.total_skipped > result.skipped_details.length && (
                    <span style={{ fontWeight: 400 }}>
                      {" "}— showing {result.skipped_details.length} of {result.total_skipped}
                    </span>
                  )}
                </h4>
                <ul style={{ paddingLeft: 18, fontSize: "11.5px", color: "#78350f", margin: 0 }}>
                  {result.skipped_details.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.errors && result.errors.length > 0 && (
              <div style={{
                background: "#fff3e0", borderRadius: "8px", padding: "12px 14px",
                maxHeight: 160, overflowY: "auto", marginBottom: "12px",
              }}>
                <h4 style={{ marginBottom: 8, fontSize: "12px", color: "#92400e" }}>⚠️ Errors</h4>
                <ul style={{ paddingLeft: 18, fontSize: "11.5px", color: "#78350f", margin: 0 }}>
                  {result.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.unmatched_officers && result.unmatched_officers.length > 0 && (
              <div style={{
                background: "#fef9c3", border: "1px solid #fde047", borderRadius: "8px",
                padding: "12px 14px", maxHeight: 240, overflowY: "auto", marginBottom: "12px",
              }}>
                <h4 style={{ marginBottom: 6, fontSize: "12px", color: "#713f12" }}>
                  👤 {result.total_unmatched_officers} officer(s) in the file have no matching user account — those paras were imported but left unassigned.
                </h4>
                <p style={{ fontSize: "11px", color: "#854d0e", marginBottom: 8 }}>
                  Create user accounts for the officers listed below, then re-upload the file to assign the paras.
                </p>
                <table style={{ width: "100%", fontSize: "11.5px", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #fde047" }}>
                      <th style={{ textAlign: "left", padding: "3px 6px", color: "#713f12", fontWeight: 700 }}>Tamil Name (in file)</th>
                      <th style={{ textAlign: "left", padding: "3px 6px", color: "#713f12", fontWeight: 700 }}>Approx. English Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.unmatched_officers.map((entry, i) => {
                      const tamil = typeof entry === "object" ? entry.tamil : entry;
                      const english = typeof entry === "object" ? entry.english : "";
                      return (
                        <tr key={i} style={{ borderBottom: "1px solid #fef08a" }}>
                          <td style={{ padding: "4px 6px", color: "#78350f" }}>{tamil}</td>
                          <td style={{ padding: "4px 6px", color: "#92400e", fontStyle: "italic" }}>{english}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {((result.rejected_records?.length || 0) + (result.skipped_records?.length || 0)) > 0 && (
              <div style={{
                background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px",
                padding: "10px 14px", marginBottom: "12px", display: "flex",
                alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
              }}>
                <span style={{ fontSize: "12px", color: "#475569" }}>
                  📄 Download the{" "}
                  <strong>
                    {(result.rejected_records?.length || 0) + (result.skipped_records?.length || 0)}
                  </strong>{" "}
                  skipped &amp; rejected row(s) to review, fix and re-upload.
                </span>
                <button
                  className="btn-outline"
                  onClick={downloadReport}
                  data-testid="bulk-download-report-btn"
                  style={{
                    padding: "7px 16px", borderRadius: "7px", fontWeight: 600,
                    fontSize: "12.5px", cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  ⬇ Download CSV
                </button>
              </div>
            )}

            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => { onDone(); onClose(); }}
                data-testid="bulk-done-btn"
                style={{
                  background: "#1e3a8a", color: "#fff", border: "none",
                  padding: "8px 24px", borderRadius: "7px", fontWeight: 600, fontSize: "13px",
                }}
              >
                ✓ Done
              </button>
            </div>
          </>
        )}
        </div>
      </div>
    </div>,
    document.body
  );
}
